import React, { FC, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { httpRequest } from 'src/services/httpRequest';
import { useParams } from 'react-router-dom';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch, useSelector } from 'react-redux';
import { sendNotification } from 'src/utils/sendNotification';
import { RootState } from 'src/redux-store/appStore';

const CustomModal: FC<any> = ({
  title,
  IsOpen,
  setIsOpen,
  loading,
  content,
  actionButtons,
  IsButtonDisabled = false,
  messageBodyRef,
  onSave,
  applicationNumber,
  ApplicationID,
  applicationDetails,
  onSkip
}) => {
  const dispatch = useDispatch();
  const socket = useSelector((store: RootState) => store.socket.socket);
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const currentPermitDetails = useSelector(
    (state: RootState) => state?.currentPermit?.currentPermitDetails
  );
  const { permit, applicationID } = useParams();
  const isSendMessageModal = title === 'Send Message';
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const isProcessing = isSendMessageModal ? isSendingMessage : loading;
  const showSendMessageOverlay = isSendMessageModal && isSendingMessage;

  useEffect(() => {
    if (!IsOpen) {
      setIsSendingMessage(false);
    }
  }, [IsOpen]);

  const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
      padding: theme.spacing(2)
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(1)
    }
  }));

  const sxProps = {
    ml: 2,
    border: '1px solid primary',
    '&:hover': {
      borderColor: 'primary'
    }
  };

  const handleClose = () => {
    if (isProcessing) {
      return;
    }
    setIsOpen(false);
  };

  const handleSendMessage = async () => {
    if (isSendingMessage) {
      return;
    }

    const messageText = messageBodyRef?.current?.trim() ?? '';
    if (messageText.length > 0) {
      setIsSendingMessage(true);
      const payload = {
        message: messageText,
        title: `Application ${applicationNumber}: New Message`,
        ApplicationID: Number(applicationID || ApplicationID),
        PermitID: Number(permit)
      };
      try {
        const response = await httpRequest('POST', `/email/send`, payload);
        if (response?.status === 'success') {
          sendNotification(socket, {
            UserID: applicationDetails?.UserID,
            SenderID: userDetails?.UserID,
            TownshipID: currentPermitDetails.townshipId,
            ApplicationID: Number(applicationID),
            PermitID: Number(permit),
            Title: `Message - ${applicationNumber}`,
            Message: messageText,
            ApplicationNumber: applicationNumber
          });
          messageBodyRef.current = '';
          setIsOpen(false);
          toast.success('Message sent successfully.');
        } else if (response?.type !== 'NETWORK_ERROR') {
          toast.error(response?.message);
          dispatch(handleError(response?.message));
        }
      } finally {
        setIsSendingMessage(false);
      }
    } else {
      toast.error("Message can't be empty.");
      dispatch(handleError("Message can't be empty."));
    }
  };

  return (
    <React.Fragment>
      <BootstrapDialog
        onClose={(_, reason) => {
          if (isProcessing && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
            return;
          }
          handleClose();
        }}
        aria-labelledby="customized-dialog-title"
        open={IsOpen}
        className={
          `${title === 'EIN (Employer Identification Number) Information'
            ? 'custom_modal_component'
            : ''} custom_modal_container`
        }
        PaperProps={{
          sx: { position: 'relative', overflow: 'hidden' }
        }}
      >
        {showSendMessageOverlay && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              px: 3,
              bgcolor: 'rgba(255, 255, 255, 0.96)'
            }}
          >
            <CircularProgress size={44} thickness={4} />
            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
              Sending message
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              maxWidth={320}
            >
              Please wait while your message is delivered to the applicant.
            </Typography>
          </Box>
        )}
        <DialogTitle
          sx={{
            m: 0,
            px: 2,
            fontWeight: 600,
            fontSize: '18px',
          }}
          id="customized-dialog-title"
        >
          {title}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isProcessing}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 12,
            color: theme.palette.grey[500]
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>{content}</Box>
          <Box display={'flex'} justifyContent={'end'} mt={2}>
            {actionButtons &&
              actionButtons?.length &&
              actionButtons?.map((value, index) => {
                const isPrimaryAction = index === 1;
                const showActionSpinner =
                  isPrimaryAction && isProcessing && !isSendMessageModal;

                return (
                  <Button
                    key={index}
                    variant={index === 0 ? 'outlined' : 'contained'}
                    color="primary"
                    className={isPrimaryAction ? 'disable-btn' : null}
                    onClick={() => {
                      if (isProcessing && isPrimaryAction) {
                        return;
                      }
                      if (index === 0) {
                        if (value === 'Skip & Submit' && onSkip) {
                          onSkip();
                        } else {
                          handleClose();
                        }
                        return;
                      }
                      if (isSendMessageModal) {
                        handleSendMessage();
                      } else {
                        onSave?.();
                      }
                    }}
                    disabled={
                      isProcessing ||
                      (isPrimaryAction && IsButtonDisabled && !isProcessing)
                    }
                    sx={{
                      ...sxProps,
                      ...(isPrimaryAction &&
                        !isSendMessageModal && {
                          position: 'relative',
                          minWidth: 96
                        })
                    }}
                  >
                    {index === 0 ? (
                      value
                    ) : showActionSpinner ? (
                      <>
                        <Box component="span" sx={{ visibility: 'hidden' }}>
                          {value}
                        </Box>
                        <CircularProgress
                          size={20}
                          color="inherit"
                          sx={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)'
                          }}
                        />
                      </>
                    ) : (
                      value
                    )}
                  </Button>
                );
              })}
          </Box>
        </DialogContent>
      </BootstrapDialog>
    </React.Fragment>
  );
};

export default CustomModal;
